import { t } from '@i18n/i18n';
import emailValidatorTranslations from '../../i18n/translations/blocks/email-validator/email-validator.json';
import getTimestamp from '../../utils/timestamp';

import React, { useState, useEffect, useCallback } from 'react';
import cx from 'classnames';
import EmailIcon from '@images/icons/email-icon.webp';
import CloseIcon from '@images/icons/close-icon.png';
import './styles/email-validator.scss';

import LangButton from '../../i18n/LangButton';

import { isValidEmail } from '../../utils/validators';
import { registerUrl, APIBaseUrl, recaptcha_site_key, isEnInBuild, isProdBuild } from '../../i18n/environments';
import { useI18nInitNamespace } from '../../i18n/useI18nInitNamespace';
import UploadIcon from '../../images/free-list-verifier/upload.png';
import UploadDarkIcon from '../../images/free-list-verifier/upload_dark.webp';
import Invalid from '../../images/free-list-verifier/invalid.png';
import Tooltip from '../../images/free-list-verifier/tooltip.png';
import CatchAll from '../../images/free-list-verifier/catchall.png';
import ActivityData from '../../images/free-list-verifier/activityData.png';
import Abuse from '../../images/free-list-verifier/abuse.png';
import NotFound from '../../images/free-list-verifier/notFound.png';
import DoNotMail from '../../images/free-list-verifier/notMail.png';
import Found from '../../images/free-list-verifier/found.png';
import FileIcon from '../../images/free-list-verifier/file.png';
import Loading from '../../components/loading/loading';
import CloudflareImage from '../../components/cloudflare-image/CloudflareImage';
import '@styles/generic/recaptcha.scss';
import '@styles/generic/recaptcha.scss';
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

const recaptchaSingleEmailValidationAction = 'FREE_VALIDATION';
const recpatchaEmailListValidationAction = 'UPLOAD_VALIDATION';

const EmailValidator = ({
  title = null,
  headText = null,
  description = null,
  additionalClass = null,
  children = null,
  isNewDesign = false,
  formClass = null,
  formTitle = null,
  isSpecialPage = null,
  successABTest = null,
  limitReachedAb = null,
  freeEmailVerifierBTest = false,
}) => {
  useI18nInitNamespace('emailValidator', emailValidatorTranslations, true);
  const allowedFileExtensions = ['.csv', '.xls', '.xlsx', '.txt'];
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    showResults: false,
    loading: false,
    limit: false,
  });
  const [results, setResults] = useState({});
  const [validEmail, setValidEmail] = useState(true);

  const [uploadResults, setUploadResults] = useState({});
  const [isJsEnabled, setIsJsEnabled] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileID, setFileID] = useState('');
  const [limitReachedError, setLimitReachedError] = useState(false);
  const [hasRequestFailed, setHasRequestFailed] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState('');
  const [invalidFileTypeError, setInvalidFileTypeError] = useState(false);
  const [hideForm, setHideForm] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const [confirmedEmail, setConfirmedEmail] = useState('');


  useEffect(() => {
    setIsJsEnabled(true);
  }, []);

  const onVerify = useCallback((token) => {
    setRecaptchaToken(token);
  }, []);

  const doRefreshReCaptcha = () => {
    setRefreshReCaptcha((r) => !r);
  };

  useEffect(() => {
    const interval = 120000;

    const intervalId = setInterval(() => {
      doRefreshReCaptcha();
    }, interval);

    return () => clearInterval(intervalId);
  }, [doRefreshReCaptcha]);

  useEffect(() => {
    const getValidationResultsData = async () => {
      const url = `${APIBaseUrl}/validation/trial-email-lists/${fileID}/`;
      setHasRequestFailed(false);
      try {
        const response = await fetch(url);

        if (response && fileID) {
          const data = await response.json();
          if (data) {
            setUploadResults(data);

            if (data.status === 'finished' || data.status === 'error') {
              setUploadLoading(false);
              setFileID('');
            }
            if (data.status === 'finished') {
              setHideForm(true);
            }
          }
        }
      } catch (err) {
        setUploadLoading(false);
        if (!uploadResults.error_message) {
          setHasRequestFailed(true);
        }

        return err;
      }
    };

    const interval = setInterval(() => {
      if (fileID && uploadLoading) {
        getValidationResultsData();
      } else if (!uploadLoading) {
        clearInterval(interval);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [uploadResults, uploadLoading, fileID]);

  const updateFormData = (name, value) => {
    setValidEmail(isValidEmail(value) || value.length === 0 ? true : false);
    setFormData({ ...formData, [name]: value });
  };

  const handleUpload = async (e) => {
    const pathIndex = e.target.value.lastIndexOf('\\');
    const uploadValue = e.target.value.substring(pathIndex + 1);
    setUploadLoading(true);
    setFileName(uploadValue);
    setFormData({ ...formData, email: '', showResults: false });
    setLimitReachedError(false);
    setHasRequestFailed(false);
    await callFreeUpload(e, recaptchaToken);
    doRefreshReCaptcha();
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'free_email_list_verifier_submit',
      });
    }
  };

  const callFreeUpload = async (event, token) => {
    setRecaptchaError('');
    setInvalidFileTypeError(false);
    const uploadData = new FormData();
    const fileName = event.target.files[0].name;
    const fileExtension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    setFormData({ ...formData, limit: false });
    if (allowedFileExtensions.includes(fileExtension)) {
      uploadData.append('file', event.target.files[0]);
      uploadData.append('fullName', formData.fullName);
      uploadData.append('recaptcha', token);
      uploadData.append('recaptcha_action', recpatchaEmailListValidationAction);

      try {
        const url = `${APIBaseUrl}/validation/trial-email-lists/`;
        const response = await fetch(url, { method: 'POST', body: uploadData });
        if (response) {
          if (response.status === 403) {
            setUploadLoading(false);
            setRecaptchaError('Recaptcha validation failed');
          } else {
            const data = await response.json();
            if (data) {
              setFileID(data.file_id);
            }
          }
        }
      } catch (err) {
        setLimitReachedError(true);
        setUploadLoading(false);
        return err;
      }
    } else {
      setInvalidFileTypeError(true);
      setUploadLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormData({ ...formData, loading: true, limit: false });
    setUploadResults({});
    setLimitReachedError(false);
    setUploadLoading(false);
    setFileID('');
    setRecaptchaError('');
    setInvalidFileTypeError(false);

    await callFreeValidation(recaptchaToken);
    setConfirmedEmail(formData.email)
    doRefreshReCaptcha();
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'free_email_verifier_submit',
      });
    }
  };

  const callFreeValidation = async (token) => {
    try {
      const payload = {
        email: formData.email.toLowerCase(),
        fullName: formData.fullName,
        timestamp: getTimestamp(formData.email.toLowerCase()),
        recaptcha: token,
        recaptcha_action: recaptchaSingleEmailValidationAction,
      };
      const url = `${APIBaseUrl}/validation/trial-email/`;
      const headers = {
        'Content-Type': 'application/json',
      };
      const response = await fetch(url, { method: 'POST', body: JSON.stringify(payload), headers });
      const data = await response.json();
      if (response.status === 429) {
        setFormData({ ...formData, loading: false, limit: true, showResults: false });
        return;
      }
      if (response.status === 403) {
        setFormData({ ...formData, loading: false, limit: true, showResults: false });
        return;
      }
      data && setResults(data);
      setValidEmail(true);
      setHideForm(true);
      setFormData({ ...formData, loading: false, limit: false, showResults: true });
    } catch (error) {
      setFormData({ ...formData, loading: false, limit: true, showResults: false });
      setValidEmail(true);
      setHideForm(false);
    }
    if (!isSpecialPage) {
      scrollToSection('#results-container');
    }
  };

  const getStatusClassName = (status = '') => {
    switch (status.toLowerCase()) {
      case 'valid':
        return 'valid';
      case 'invalid':
      case 'spamtrap':
      case 'do_not_mail':
      case 'donotmail':
      case 'abuse':
        return 'invalid';
      default:
        return 'unknown';
    }
  };

  const scrollToSection = (id) => {
    const target = document.querySelector(id);
    const isIE11 = window && !!window.MSInputMethodContext && document && !!document.documentMode;

    if (!isIE11) {
      target.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptcha_site_key} useEnterprise={true}>
      <div className={cx('email-validator', additionalClass)}>
        <GoogleReCaptcha onVerify={onVerify} refreshReCaptcha={refreshReCaptcha} />
        <div className={`shell ${formClass}`} style={{ maxWidth: hideForm && isSpecialPage && '550px' }}>
          <div className="email-validator__inner">
            <div className="email-validator__head">
              <h2 id="validator_component_header" className="h2">
                {title}
              </h2>

              <p id="validator_component_head_text">{headText}</p>
              <p id="validator_component_description">{description}</p>
            </div>

            {isNewDesign ? (
              <div className={'new-email-validator__form'}>
                <form onSubmit={(e) => handleSubmit(e)} id="verify-email-form">
                  <label htmlFor="file" className="upload-file_label">
                    <span className={'email-validator__label_image_container'}>
                      <CloudflareImage id="validator_component_upload_img" src={UploadDarkIcon} alt="" width="25" height="26" />
                    </span>
                    <span id="validator_component_upload_text">{t('Upload_your')}</span>
                  </label>
                  <input
                    onChange={(e) => {
                      if (e.target.value) {
                        handleUpload(e);
                      }
                    }}
                    onClick={(e) => {
                      if (formData.showResults || hasRequestFailed) {
                        e.target.value = null;
                      }
                    }}
                    type="file"
                    name="file"
                    id="file"
                    disabled={uploadLoading}
                    accept=".csv, .xls, .xlsx, .txt"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor={'full-name'} className={'full-name-label'}>
                    <span className={'hidden'}>{t('Full_name')}</span>
                  </label>
                  <input
                    type={'text'}
                    aria-label={'Type your full name'}
                    id={'full-name'}
                    name={'full-name'}
                    value={formData.fullName}
                    className={'full-name'}
                    placeholder={'John Doe'}
                    onChange={(e) => updateFormData('fullName', e.target.value)}
                  />
                  {/* <LangButton
                  type="submit"
                  modifier={`btn--primary email-validator__form-btn ${
                    !validEmail || formData.email.trim().length === 0 || formData.loading ? 'disabled' : ''
                  }`}
                  id="results-container"
                >
                  <i className="ico-svg-send">
                    <svg id="validator_component_verify_img" width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
                      <g stroke="currentColor" strokeWidth="3" fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M24.27 2.098L2.775 10.922c-1.12.459-.999 2.082.176 2.372L8.87 14.75" />
                        <path d="M16.225 11.63l-3.087 3.108a2.666 2.666 0 00-.769 2.055l.52 7.864c.085 1.274 1.8 1.618 2.369.474l10.607-21.32c.514-1.033-.527-2.152-1.596-1.713" />
                      </g>
                    </svg>
                  </i>
                  {formData.loading ? t('Loading') : t('Verify')}
                </LangButton> */}
                </form>
              </div>
            ) : (
              <div
                className="email-validator__form"
                style={{
                  display: hideForm && isSpecialPage && 'none',
                  borderBottomLeftRadius: freeEmailVerifierBTest && limitReachedError && '0',
                  borderBottomRightRadius: freeEmailVerifierBTest && limitReachedError && '0',
                }}
              >
                <form onSubmit={(e) => handleSubmit(e)} id="verify-email-form">
                  {formClass && <p>{formTitle}</p>}
                  {!isEnInBuild ? (
                    <>
                      <label htmlFor="file" className="upload-file_label">
                        <CloudflareImage id="validator_component_upload_img" src={UploadIcon} alt="" width="25" height="26" />
                        <span id="validator_component_upload_text">{t('Upload_your')}</span>
                      </label>
                      <input
                        onChange={(e) => {
                          if (e.target.value) {
                            handleUpload(e);
                          }
                        }}
                        onClick={(e) => {
                          if (formData.showResults || hasRequestFailed) {
                            e.target.value = null;
                          }
                        }}
                        type="file"
                        name="file"
                        id="file"
                        disabled={uploadLoading}
                        accept=".csv, .xls, .xlsx, .txt"
                        style={{ display: 'none' }}
                      />
                      <div id="validator_component_divider" className="upload-divider">
                        {t('OR')}
                      </div>
                    </>
                  ) : (
                    ''
                  )}
                  <label htmlFor="email-validator">
                    <span className="hidden">{t('Email')}</span>
                  </label>
                  <input
                    type="email"
                    aria-label="Type your email"
                    id="email-validator"
                    name="email-validator"
                    value={formData.email}
                    disabled={uploadLoading}
                    className={cx({ invalid: !validEmail })}
                    placeholder="email@example.com"
                    onChange={(e) => updateFormData('email', e.target.value)}
                  />
                  <label htmlFor={'full-name'}>
                    <span className={'hidden'}>{t('Full_name')}</span>
                  </label>
                  <input
                    type={'text'}
                    aria-label={'Type your full name'}
                    id={'full-name'}
                    name={'full-name'}
                    value={formData.fullName}
                    className={'full-name'}
                    placeholder={'John Doe'}
                    onChange={(e) => updateFormData('fullName', e.target.value)}
                  />

                  <LangButton
                    type="submit"
                    modifier={`btn--primary email-validator__form-btn ${
                      !validEmail || formData.email.trim().length === 0 || formData.loading ? 'disabled' : ''
                    }`}
                    id="results-container"
                  >
                    <i className="ico-svg-send">
                      <svg
                        id="validator_component_verify_img"
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g
                          stroke="currentColor"
                          strokeWidth="3"
                          fill="none"
                          fillRule="evenodd"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M24.27 2.098L2.775 10.922c-1.12.459-.999 2.082.176 2.372L8.87 14.75" />
                          <path d="M16.225 11.63l-3.087 3.108a2.666 2.666 0 00-.769 2.055l.52 7.864c.085 1.274 1.8 1.618 2.369.474l10.607-21.32c.514-1.033-.527-2.152-1.596-1.713" />
                        </g>
                      </svg>
                    </i>
                    {formData.loading ? t('Loading') : t('Verify')}
                  </LangButton>
                </form>
              </div>
            )}

            {(formData.limit || (limitReachedError && !uploadLoading)) &&
              (successABTest && Array.isArray(successABTest) ? (
                !freeEmailVerifierBTest ? (
                  // Case 1: successABTest exists AND freeEmailVerifierBTest is false
                  <div className="results__status">
                    <p id="validator_component_reached">{t('You_reached')}</p>
                    <div className="results__actions">
                      <LangButton
                        id="validator_component_result_btn"
                        modifier="btn--primary"
                        externalLink={true}
                        onClick={(e) => limitReachedAb(e, '', true, ({ param }) => (window.location.href = registerUrl + param))}
                      >
                        {t('Get_Started')}
                      </LangButton>
                      <p id="validator_component_sign_up">{t('Sign_up')}</p>
                    </div>
                  </div>
                ) : (
                  // Case 2: successABTest exists AND freeEmailVerifierBTest is true
                  <div className="results__status_ab">
                    <p className="ab_error-message" id="validator_component_reached">
                      <span></span>
                      {t('email_verifier_testb1')}
                    </p>
                    <p className="ab_title">{t('email_verifier_testb2')}</p>
                    <div className="results__actions">
                      <ul>
                        <li>
                          <span className="checkmark"></span>
                          {t('email_verifier_testb3')}
                        </li>
                        <li>
                          <span className="checkmark"></span>
                          {t('email_verifier_testb4')}
                        </li>
                        <li style={{ marginBottom: '15px' }}>
                          <span className="checkmark"></span>
                          {t('email_verifier_testb5')}
                        </li>
                      </ul>
                      <LangButton
                        id="validator_component_result_btn"
                        modifier="btn--primary"
                        externalLink={true}
                        onClick={(e) => limitReachedAb(e, '', true, ({ param }) => (window.location.href = registerUrl + param))}
                      >
                        {t('email_verifier_testb6')}
                      </LangButton>
                      <p style={{ fontSize: '14px', color: '#000', marginTop: '10px' }} id="validator_component_sign_up">
                        {t('email_verifier_testb7')}
                      </p>
                    </div>
                  </div>
                )
              ) : (
                // Case 3: successABTest does NOT exist
                <div className="results__status">
                  <p id="validator_component_reached">{t('You_reached')}</p>
                  <div className="results__actions">
                    <LangButton
                      id="validator_component_result_btn"
                      modifier="btn--primary"
                      externalLink={true}
                      onClick={(e) => limitReachedAb(e, '', true, ({ param }) => (window.location.href = registerUrl + param))}
                    >
                      {t('Get_Started')}
                    </LangButton>
                    <p id="validator_component_sign_up">{t('Sign_up')}</p>
                  </div>
                </div>
              ))}

            {formData.loading && (
              <div className="results__status">
                <p id="validator_component_loading">{t('Validating_Please')}</p>
              </div>
            )}

            {uploadLoading && (
              <div>
                <p id="validator_component_list" className="validating-list">
                  {t('Validating_list')}
                </p>
                <Loading height="30px" />
              </div>
            )}

            {uploadResults.status === 'error' && !uploadLoading && !limitReachedError && !hasRequestFailed && (
              <p id="validator_component_error_1" className="error-message">
                {uploadResults.error_message}
              </p>
            )}

            {hasRequestFailed && !uploadLoading && (
              <p id="validator_component_error_3" className="error-message">
                {t('This_took')}
              </p>
            )}

            {recaptchaError && !uploadLoading && (
              <p id="validator_component_error_4" className="error-message">
                {recaptchaError}
              </p>
            )}

            {invalidFileTypeError && !uploadLoading && (
              <p id="validator_component_error_5" className="error-message">
                {t('Invalid_file')}
              </p>
            )}

            {!isSpecialPage
              ? !validEmail &&
                !uploadLoading && (
                  <p id="validator_component_error_6" className="error-message">
                    {t('valid_email')}
                  </p>
                )
              : !validEmail &&
                !uploadLoading &&
                !hideForm && (
                  <p id="validator_component_error_6" className="error-message">
                    {t('valid_email')}
                  </p>
                )}

            {uploadResults.status === 'finished' &&
              !uploadLoading &&
              !limitReachedError &&
              !hasRequestFailed &&
              !invalidFileTypeError &&
              hideForm && (
                <div className="results__panel">
                  {isSpecialPage && (
                    <button
                      type="button"
                      className="close__panel"
                      onClick={() => {
                        setHideForm(false);
                        setFormData({
                          email: '',
                          fullName: '',
                          showResults: false,
                          loading: false,
                          limit: false,
                        });
                        setUploadResults({});
                      }}
                    >
                      Try Again
                    </button>
                  )}
                  <h4 id="validator_component_result_header" className="results__panel-title">
                    {t('Results')}
                  </h4>
                  <p className="results__panel-filename">
                    <CloudflareImage id="validator_component_result_img_1" src={FileIcon} alt="" width="20" height="20" />
                    {fileName}
                  </p>
                  <p id="validator_component_result_title" className="results__title">
                    {t('Your_list_is')}{' '}
                    <span
                      id="validator_component_result_percentage"
                      className={`${
                        uploadResults.total_risky_percentage === 0 ? 'results__percentage-success' : 'results__percentage-error'
                      }`}
                    >
                      {uploadResults.total_risky_percentage}%
                    </span>{' '}
                    {t('Your_list_is_1')}
                  </p>
                  <div className="upload-results__container">
                    <div className="upload-results__item">
                      <p className="upload-results__title">
                        <CloudflareImage id="validator_component_result_img_2" src={Invalid} alt="" width="20" height="20" />
                        <span id="validator_component_result_invalid" className="item-title">
                          {t('Invalid')}
                        </span>
                        <CloudflareImage
                          id="validator_component_result_img_3"
                          className="tooltip"
                          src={Tooltip}
                          alt=""
                          width="14"
                          height="14"
                        />
                        <span id="validator_component_result_invalid_2" className="tooltip__text">
                          {t('Invalid_addresses')}
                        </span>
                      </p>
                      <p
                        id="validator_component_result_invalid_percentage"
                        className={`percentage ${uploadResults.invalid_percentage === 0 && 'special'}`}
                      >
                        {uploadResults.invalid_percentage}%
                      </p>
                    </div>
                    <div className="upload-results__item">
                      <p className="upload-results__title">
                        <CloudflareImage id="validator_component_result_img_4" src={CatchAll} alt="" width="20" height="20" />
                        <span id="validator_component_result_catch_all" className="item-title">
                          {t('Catch_all')}
                        </span>
                        <CloudflareImage
                          id="validator_component_result_img_5"
                          className="tooltip"
                          src={Tooltip}
                          alt=""
                          width="14"
                          height="14"
                        />
                        <span id="validator_component_result_incoming_email" className="tooltip__text">
                          {t('Incoming_emails')}
                        </span>
                      </p>
                      <p
                        id="validator_component_result_percentage_2"
                        className={`percentage ${uploadResults.catch_all_percentage === 0 && 'special'}`}
                      >
                        {uploadResults.catch_all_percentage}%
                      </p>
                    </div>
                    <div className="upload-results__item">
                      <p className="upload-results__title">
                        <CloudflareImage id="validator_component_result_img_6" src={ActivityData} alt="" width="20" height="14" />
                        <span id="validator_component_result_activity_data" className="item-title">
                          {t('Activity_data')}
                        </span>
                        <CloudflareImage
                          id="validator_component_result_img_7"
                          className="tooltip"
                          src={Tooltip}
                          alt=""
                          width="14"
                          height="14"
                        />
                        <span id="validator_component_unique_score" className="tooltip__text">
                          {t('Unique_score')}
                        </span>
                      </p>
                      <p id="validator_component_result_percentage_special" className="percentage special">
                        {uploadResults.activity_data_percentage}%
                      </p>
                    </div>
                  </div>
                  <div className="upload-results__container">
                    <div className="upload-results__item">
                      <p className="upload-results__title second-column">
                        <CloudflareImage id="validator_component_result_img_8" src={Abuse} alt="" width="16" height="20" />
                        <span id="validator_component_result_abuse" className="item-title">
                          {t('Abuse')}
                        </span>
                        <CloudflareImage
                          id="validator_component_result_img_9"
                          className="tooltip"
                          src={Tooltip}
                          alt=""
                          width="14"
                          height="14"
                        />
                        <span id="validator_component_result_tough_valid" className="tooltip__text">
                          {t('Though_valid')}
                        </span>
                      </p>
                      <p className="mobile-second__column">
                        <CloudflareImage
                          id="validator_component_result_img_10"
                          src={uploadResults.abuse ? Found : NotFound}
                          alt=""
                          width="25"
                          height="25"
                        />
                        <span
                          id="validator_component_result_abuse_found"
                          className={`second-column__subtitle ${uploadResults.abuse && 'error'}`}
                        >
                          {uploadResults.abuse ? t('Found') : t('Not_found')}
                        </span>
                      </p>
                    </div>
                    <div className="upload-results__item">
                      <p className="upload-results__title second-column">
                        <CloudflareImage id="validator_component_result_img_11" src={DoNotMail} alt="" width="20" height="16" />
                        <span id="validator_component_result_not_mail" className="item-title">
                          {t('Not_mail')}
                        </span>
                        <CloudflareImage
                          id="validator_component_result_img_12"
                          className="tooltip"
                          src={Tooltip}
                          alt=""
                          width="14"
                          height="14"
                        />
                        <span id="validator_component_result_never_send" className="tooltip__text">
                          {t('Never_send')}
                        </span>
                      </p>
                      <p className="mobile-second__column">
                        <CloudflareImage
                          id="validator_component_result_img_13"
                          src={uploadResults.do_not_mail ? Found : NotFound}
                          alt=""
                          width="25"
                          height="25"
                        />
                        <span
                          id="validator_component_result_img_dont_mail"
                          className={`second-column__subtitle ${uploadResults.do_not_mail && 'error'}`}
                        >
                          {uploadResults.do_not_mail ? t('Found') : t('Not_found')}
                        </span>
                      </p>
                    </div>
                    <div className="upload-results__item">
                      <p className="upload-results__title second-column">
                        <CloudflareImage id="validator_component_result_img_14" src={Found} alt="" width="20" height="20" />
                        <span id="validator_component_result_spam_trap" className="item-title">
                          {t('Spam_trap')}
                        </span>
                        <CloudflareImage
                          id="validator_component_result_img_15"
                          className="tooltip"
                          src={Tooltip}
                          alt=""
                          width="14"
                          height="14"
                        />
                        <span id="validator_component_result_use_these" className="tooltip__text">
                          {t('Use_these')}
                        </span>
                      </p>
                      <p className="mobile-second__column">
                        <CloudflareImage
                          id="validator_component_result_img_16"
                          src={uploadResults.spam_trap ? Found : NotFound}
                          alt=""
                          width="25"
                          height="25"
                        />
                        <span
                          id="validator_component_result_spam_error"
                          className={`second-column__subtitle ${uploadResults.spam_trap && 'error'}`}
                        >
                          {uploadResults.spam_trap ? t('Found') : t('Not_found')}
                        </span>
                      </p>
                    </div>
                  </div>
                  <p id="validator_component_result_based_on" className="space-bottom">
                    *{t('Based_on')}
                  </p>
                  {successABTest && successABTest[1]}
                </div>
              )}

            {formData.showResults && (
              <div className="results__panel">
                <button
                  id="validator_component_result_close"
                  type="button"
                  className="close__panel"
                  onClick={() => {
                    updateFormData('showResults', false);
                    scrollToSection('#email-validator');
                    setHideForm(false);
                    setFormData({
                      email: '',
                      fullName: '',
                      showResults: false,
                      loading: false,
                      limit: false,
                    });
                    setUploadResults({});
                  }}
                >
                  {!isSpecialPage ? (
                    <CloudflareImage
                      id="validator_component_close_icon"
                      src={CloseIcon}
                      alt="Close test results"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    'Try again'
                  )}
                </button>

                <div className="logo__section">
                  <CloudflareImage
                    id="validator_component_result_logo"
                    src={EmailIcon}
                    alt="email"
                    loading="lazy"
                    decoding="async"
                    width="50"
                  />
                </div>
                <div className="email__section">
                  <p id="validator_component_result_email">{confirmedEmail}</p>
                </div>

                <div className="result__wrapper">
                  <div className="result__field">
                    <div className="field__label" id="validator_component_status_label">
                      {t('STATUS')}
                    </div>
                    <div className={`field__data ${getStatusClassName(results.status)}`}>
                      <p id="validator_component_status">{results.status}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_sub_status_label">
                      {t('SUB_STATUS')}
                    </div>
                    <div className={`field__data ${getStatusClassName(results.status)}`}>
                      <p id="validator_component_sub_status">{results.sub_status === '' ? 'None' : results.sub_status}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_free_label">
                      {t('FREE_EMAIL')}
                    </div>
                    <div className="field__data">
                      <p id="validator_component_free">{!results['free_email'] ? 'Unknown' : 'True'}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_did_you_label">
                      {t('DID_YOU')}
                    </div>
                    <div className="field__data">
                      <p id="validator_component_did_you">{!results['did_you_mean'] ? '' : results['did_you_mean']}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_account_label">
                      {t('ACCOUNT')}
                    </div>
                    <div className="field__data">
                      <p id="validator_component_account">{results.account}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_domain_label">
                      {t('DOMAIN')}
                    </div>
                    <div className="field__data">
                      <p id="validator_component_domain">{results.domain}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_age_label">
                      {t('DOMAIN_AGE')}
                    </div>
                    <div className="field__data">
                      <p id="validator_component_age">{!results['domain_age_days'] ? 'Unknown' : results['domain_age_days']}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_stmp_label">
                      {t('SMTP_PROVIDER')}
                    </div>
                    <div className="field__data">
                      <p id="validator_component_stmp">{!results['smtp_provider'] ? 'Unknown' : results['smtp_provider']}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_mx_found_label">
                      {t('MX_FOUND')}
                    </div>
                    <div className="field__data">
                      <p id="validator_component_mx_found">{!results['mx_found'] ? 'Unknown' : results['mx_found']}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_mx_record_label">
                      {t('MX_RECORD')}
                    </div>
                    <div className="field__data">
                      <p id="validator_component_mx_record">{!results['mx_record'] ? 'Unknown' : results['mx_record']}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_first_name_label">
                      {t('FIRST_NAME')}
                    </div>
                    <div className="field__data">
                      <p id="validator_component_first_name">{!results.firstname ? 'Unknown' : results.firstname}</p>
                    </div>
                  </div>

                  <div className="result__field">
                    <div className="field__label" id="validator_component_last_name_label">
                      {t('LAST_NAME')}
                    </div>
                    <div className="field__data">
                      <p id="validator_component_last_name">{!results.lastname ? 'Unknown' : results.lastname}</p>
                    </div>
                  </div>
                </div>
                {successABTest && successABTest[0]}
              </div>
            )}

            {children && <div className="email-validator__entry">{children}</div>}
          </div>
        </div>
      </div>
    </GoogleReCaptchaProvider>
  );
};

export default EmailValidator;
